import dayjs from 'dayjs'
import type { TChangePayload, TPreset } from '@winnin/ds-date-range-picker'

import { BEGINNING_OF_TIME } from 'consts/filterOptions'
import type { TranslateFunction, TranslateOptions } from 'lib/tolgee'
import FeatureFlagsStore from 'stores/feature-flags'
import { isDayOutsideRange, toValidDate } from 'utils/date-picker'
import getDateRange from 'utils/getDateRange'

import { ALL_TIME_VALUE, CUSTOM_OPTION_KEY, OPTION_LABEL_TRANSLATIONS } from './custom-date-picker-range.consts'
import type { TInitialSelection, TOption, TSelectedOption } from './custom-date-picker-range.types'

/** Resolves a preset label to its Tolgee entry, falling back to the raw label. */
const getOptionLabelTranslation = (label: string): TranslateOptions =>
  OPTION_LABEL_TRANSLATIONS[label] ?? { key: `CustomDatePickerRange-option${label}`, defaultValue: label }

/**
 * Maps the product `options` onto the DS presets sidebar. The `custom` option is
 * the manual-range fallback (never shown as a preset), and the `all` option
 * becomes a `clearsValue` ("All time") reset preset. Labels go through Tolgee
 * with the same `CustomDatePickerRange-option<Label>` keys the old view used.
 */
export const optionsToPresets = (options: TOption[], t: TranslateFunction): TPreset[] => {
  const today = dayjs().toDate()

  return options.flatMap<TPreset>(option => {
    if (option.key === CUSTOM_OPTION_KEY) {
      return []
    }

    const label = t(getOptionLabelTranslation(option.label))

    if (option.value === ALL_TIME_VALUE) {
      return [{ key: option.key, label, clearsValue: true }]
    }

    const start = toValidDate(option.start)

    if (!start) {
      return []
    }

    return [{ key: option.key, label, start, end: today }]
  })
}

/**
 * Derives the DS dropdown's initial `value` + active preset from the product
 * `selected` option. Mirrors the old model: `all` → all-time, `custom` → parse
 * the `uploadDate` URL param, a preset → `{ start, today }`.
 */
export const resolveInitialSelection = (selected: TSelectedOption, search: string): TInitialSelection => {
  if (selected?.value === ALL_TIME_VALUE) {
    return { value: null, selectedPresetKey: selected.key }
  }

  if (selected?.key === CUSTOM_OPTION_KEY) {
    const uploadDateParam = new URLSearchParams(search).get('uploadDate')
    const dateRangeFromString = getDateRange(uploadDateParam)

    if (uploadDateParam && dateRangeFromString) {
      return {
        value: { start: dayjs(+dateRangeFromString.gte).toDate(), end: dayjs(+dateRangeFromString.lte).toDate() },
      }
    }

    return { value: null }
  }

  if (selected?.start) {
    return {
      value: { start: toValidDate(selected.start), end: dayjs().toDate() },
      selectedPresetKey: selected.key,
    }
  }

  return { value: null }
}

/**
 * Reconstructs the product `option` to emit on Apply from the DS payload.
 * A matched preset returns that option verbatim; an empty range returns the
 * `all` option; anything else builds the `custom` option, resolving its
 * `value`/`rangeQuery` factories with the selected epoch-ms bounds.
 */
export const payloadToOption = ({ start, end, presetKey }: TChangePayload, options: TOption[]): TOption | undefined => {
  if (presetKey) {
    const matched = options.find(option => option.key === presetKey && option.key !== CUSTOM_OPTION_KEY)

    if (matched) {
      return matched
    }
  }

  if (!start || !end) {
    return options.find(option => option.value === ALL_TIME_VALUE)
  }

  const customOption = options.find(option => option.key === CUSTOM_OPTION_KEY) ?? options.at(-1)

  if (!customOption) {
    return undefined
  }

  const gteValue = String(dayjs(start).valueOf())
  const lteValue = String(dayjs(end).valueOf())

  return {
    ...customOption,
    value: typeof customOption.value === 'function' ? customOption.value(gteValue, lteValue) : customOption.value,
    rangeQuery:
      typeof customOption.rangeQuery === 'function'
        ? customOption.rangeQuery(gteValue, lteValue)
        : customOption.rangeQuery,
  } as TOption
}

export const createDefaultIsOutsideRange = (): ((day: Date) => boolean) => {
  const isTerritoryAllTimeEnabled =
    FeatureFlagsStore.isFeatureFlagEnabled('isTerritoryAllTimeEnabledUser') &&
    FeatureFlagsStore.isFeatureFlagEnabled('isTerritoryAllTimeEnabledWorkspace')

  const minDate = (isTerritoryAllTimeEnabled ? dayjs(BEGINNING_OF_TIME) : dayjs().subtract(3, 'years')).toDate()

  return (day: Date) => isDayOutsideRange(day, minDate)
}
